import { authClient } from "@/http";
import type { AuthSessionSeed } from "@/modules/auth/public";

export type LoginAuthType = "GitHub";

// 인증 서버가 정규화한 외부 identity 다. Registry 신원이 아니다. `subject` 는 GitHub 계정 id 이므로
// registry User uuid 로 쓰지 않는다. 화면이 쓰는 값은 로그인 직후 `/v1/me` 가 준다.
export type AuthenticatedIdentity = {
  version: 1;
  provider: "github";
  subject: string;
  username: string | null;
  display_name: string | null;
  email: string | null;
  email_verified: boolean;
  attributes: Record<string, unknown>;
};

export type LoginResp = {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
  auth_type: LoginAuthType;
  identity: AuthenticatedIdentity;
};

export type GithubStartResp = {
  authorize_url: string;
  state: string;
};

export type GithubLoginParams = {
  code: string;
  state: string;
};

type GithubLoginReq = GithubLoginParams & {
  auth_type: "GitHub";
};

export async function startGithubLogin(redirectUri: string): Promise<GithubStartResp> {
  // redirect_uri를 전달한다. 인증 서버는 현재 환경의 콜백 URL을 authorize_url에 포함하고 token
  // 교환에 다시 쓴다. 허용목록 밖이면 서버가 400 auth.github.redirect_uri.not_allowed로 거부한다.
  const response = await authClient.get<GithubStartResp>("/v1/auth/github/start", {
    params: { redirect_uri: redirectUri },
  });

  return response.data;
}

export async function loginWithGithubCode(params: GithubLoginParams): Promise<LoginResp> {
  const response = await authClient.post<LoginResp>("/v1/auth/github/login", {
    auth_type: "GitHub",
    code: params.code,
    state: params.state,
  } satisfies GithubLoginReq);

  return response.data;
}

// 로그인 응답은 토큰과 수명만 준다. Registry 신원(uid·member·등급·권한)은 그 직후 `/v1/me` 가
// 채운다. 처음 로그인하는 사람은 첫 `/v1/me` 호출이 계정도 생성한다.
export function createGithubSessionSeed(loginResp: LoginResp): AuthSessionSeed {
  return {
    provider: "github",
    accessToken: loginResp.access_token,
    // 인증 서버 TTL 초 단위를 AppState에서 쓰는 절대 만료 시각으로 바꾼다.
    accessTokenExpiresAt: new Date(Date.now() + loginResp.expires_in * 1000).toISOString(),
  };
}
